import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, boolean, object, string } from 'yup';

import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import { disableAutofillBg } from 'src/common/components/constants/disableAutofillBg';
import LocalizedTextField from 'src/common/components/form/LocalizedTextField';
import Icon from 'src/common/components/icons/Icon';
import { APP_ICON_IDS } from 'src/common/components/icons/iconPicker';
import { LanguageKeys } from 'src/common/constants/languages';
import { normalizeLocalizedString } from 'src/helpers/localizedStringUtils';
import { IBaseTechnicalType } from 'src/interfaces/TechnicalType';
import { ControlledSelectField } from '../components/form/controlled/ControlledSelectField';

export type FormTechnicalType = IBaseTechnicalType & {
  icon?: string;
};

interface TechnicalTypeEditorFormProps {
  handleClose?: () => void;
  handleSave: (editedItem: FormTechnicalType, id?: string) => void;
  item?: FormTechnicalType;
  withIcon?: boolean;
}

const descriptionI18nShape = {
  [LanguageKeys.en]: string(),
  [LanguageKeys.ro]: string(),
  [LanguageKeys.hu]: string(),
};

const emptyDescriptionI18n = {
  [LanguageKeys.hu]: '',
  [LanguageKeys.ro]: '',
  [LanguageKeys.en]: '',
};

const TechnicalTypeEditorForm = ({
  handleSave,
  handleClose,
  item,
  withIcon,
}: TechnicalTypeEditorFormProps) => {
  const { t } = useTranslation();
  const [editorLang, setEditorLang] = useState<LanguageKeys>(LanguageKeys.en);

  const technicalTypeSchema: ObjectSchema<FormTechnicalType> = object()
    .shape({
      id: string(),
      name: string()
        .trim()
        .matches(/^[a-z0-9-]+$/, t('form.yup.lettersNumbersDashesOnly'))
        .required(t('form.yup.genericRequired')),
      i18n: object()
        .shape({
          [LanguageKeys.en]: string().required(t('form.yup.genericRequired')),
          [LanguageKeys.ro]: string().required(t('form.yup.genericRequired')),
          [LanguageKeys.hu]: string().required(t('form.yup.genericRequired')),
        })
        .required(t('form.yup.genericRequired')),
      descriptionI18n: object().shape(descriptionI18nShape).default(undefined),
      icon: string(),
      published: boolean(),
    })
    .required();

  const defaultValues = item
    ? { ...item, descriptionI18n: item.descriptionI18n ?? emptyDescriptionI18n }
    : { published: true, descriptionI18n: emptyDescriptionI18n };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
    setValue,
  } = useForm<FormTechnicalType>({
    resolver: yupResolver(technicalTypeSchema),
    defaultValues,
  });

  const onSubmit: SubmitHandler<FormTechnicalType> = async formData => {
    handleSave(formData, item?.id);
  };

  const generateOptions = () => {
    return APP_ICON_IDS.map(k => (
      <MenuItem key={k} value={k}>
        <ListItemIcon sx={{ minWidth: 35 }}>
          <Icon icon={k} />
        </ListItemIcon>
        <ListItemText primary={k} />
      </MenuItem>
    ));
  };

  const i18nFieldError = errors.i18n?.[editorLang]?.message;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ width: '100%', maxWidth: '100%' }}
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={{ xs: withIcon ? 6 : 12 }}>
          <TextField
            sx={disableAutofillBg}
            label={t('technicalTypes.nameLabel')}
            variant="outlined"
            fullWidth
            margin="dense"
            error={!!errors['name']}
            helperText={errors['name']?.message}
            {...register('name')}
          />
        </Grid>
        {withIcon ? (
          <Grid size={{ xs: 6 }}>
            <ControlledSelectField<FormTechnicalType>
              control={control}
              name="icon"
              fullWidth
              label={t('form.icon')}
              margin="dense"
              renderOptions={generateOptions}
              withEmptyOption
              renderValue={v => (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Icon icon={v as string} fontSize="small" />
                  {v as string}
                </Box>
              )}
            />
          </Grid>
        ) : null}
        <Grid size={{ xs: 12 }}>
          <LocalizedTextField
            label={t('technicalTypes.translatedNameLabel')}
            value={watch('i18n')}
            onChange={value =>
              setValue('i18n', value, { shouldValidate: true, shouldDirty: true })
            }
            editorLang={editorLang}
            onEditorLangChange={setEditorLang}
            error={Boolean(i18nFieldError)}
            helperText={i18nFieldError}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <LocalizedTextField
            label={t('technicalTypes.descriptionLabel')}
            value={normalizeLocalizedString(
              watch('descriptionI18n') as Record<LanguageKeys, string> | undefined,
            )}
            onChange={value => setValue('descriptionI18n', value, { shouldDirty: true })}
            editorLang={editorLang}
            onEditorLangChange={setEditorLang}
            multiline
            minRows={2}
            syncInitialEditToAllLangs={false}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            sx={{
              ml: 0,
              mr: 0,
              '& .MuiSwitch-root': { ml: 0, mr: 1 },
            }}
            control={
              <Switch
                checked={watch('published') ?? true}
                onChange={e => setValue('published', e.target.checked)}
              />
            }
            label={t('system.sponsors.published')}
          />
        </Grid>

        {errors ? (
          <Grid size={{ xs: 12 }} sx={{ textAlign: 'right' }}>
            {Object.keys(errors).map(key => (
              <FormHelperText key={key} error>
                {t('form.keysWithErrors', { key: t(`form.${key}`) })}
              </FormHelperText>
            ))}
          </Grid>
        ) : null}

        <Grid size={{ xs: 12 }} sx={{ textAlign: 'right' }}>
          {!!handleClose && (
            <Button onClick={handleClose} variant="outlined" sx={{ mr: 2 }}>
              {t('base.cancel')}
            </Button>
          )}
          <Button type="submit" variant="contained" color="primary">
            {t('form.save')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TechnicalTypeEditorForm;
